import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import { useCommon } from "../../contexts/common";
import isEmail from "validator/lib/isEmail";
import { LOADING, SUCCESS, treatment_program } from "../../constants/common";
import { useBasicInfo } from "../../contexts/BasicInfo";
import { useSettings } from "../../contexts/Settings";
import LoadingButton from "@mui/lab/LoadingButton";
import { ReactComponent as ShieldExclamation } from "../../assets/Icons/shield-exclamation.svg";
import { useAuth } from "../../contexts/Auth";
import { useGeneralProfile } from "../../contexts/Organization";
import { validateName, validatePhoneNumber } from "../../helper/Validations";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Modal from "@mui/material/Modal";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { accountSettingsSchema } from "../../validations/profileValidations";
import { zodResolver } from "@hookform/resolvers/zod";
import { trackClickEvent } from "../../mixpanel";

const AccountSettings = () => {
  const initialState = {
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "",
  };
  const navigate = useNavigate();
  const [state, setState] = useState(initialState);
  const [isInvalid, setIsInvalid] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const {
      control,
      handleSubmit,
      formState: { errors, isValid },
      trigger,
      setValue,
    } = useForm({
      resolver: zodResolver(accountSettingsSchema),
      mode: "onChange",
    });

    const formValues = useWatch({ control });
  
  
    useEffect(() => {
      setState(prev => ({ ...prev, ...formValues }));
    }, [formValues]);
  

  const {
    resendEmailVerification,
    resendEmailStatus,
    account_type,
    handleDeactivateAccount,
    handleDeactivateAccountFeedback,
    isMentalHealthProfessional,
    permissions,
  } = useAuth();
  const { checkIfEmailExists, emailExists, handleState, emailExistsStatus } =
    useCommon();
  const { data, fetchInfo } = useBasicInfo();
  const { facilityInfo, fetchFacilityInfo } = useGeneralProfile();
  const { updateAccountStatus, updateAccountSettings, handleSettingsState } =
    useSettings();
  const info = account_type !== treatment_program ? data : facilityInfo;

  const handleOpenAlert = () => {
    setOpenAlert(true);
  };

  const resendEmail = () => {
    resendEmailVerification({ email: info?.email });
  };

  const handleInput = (ev) => {
    if (ev.target.name === "email") {
      handleState({ emailExists: false });
    }
    setState((prevState) => ({
      ...prevState,
      [ev.target.name]: ev.target.value,
    }));
  };

  const handlePhoneNumberInput = (fomattedNumber) => {
    setValue('phone_number', fomattedNumber.phone_number);
    setState((prev) => ({ ...prev, phone_number: fomattedNumber.phone_number }))
  }

  // const isValid = () => {
  //   return (
  //     validateName(state.first_name) &&
  //     validateName(state.last_name) &&
  //     state.email &&
  //     isEmail(state.email) &&
  //     (info?.email !== state.email ? !Boolean(emailExists) : true) &&
  //     emailExistsStatus !== LOADING &&
  //     validatePhoneNumber(state.phone_number)
  //   );
  // };

  const onSubmit = async (ev) => {
    let response = { data: { code: 0 } };
    if (info?.email !== state.email) {
      response = await checkIfEmailExists(state.email);
    }
    if (isValid && !Boolean(response.data.code)) {
      updateAccountSettings(state);
      trackClickEvent("Updated Account Settings", {
        component: "Account Settings",
        page: "Settings",
        button: "Save"
      });
    } else setIsInvalid(true);
  };

  const handleOpenDeactivateModal = () => {
    setOpenDeactivateModal(true);
  };

  const handleCloseDeactivateModal = () => {
    setOpenDeactivateModal(false);
  };

  const handleCloseFeedbackModal = async () => {
    setOpenFeedbackModal(false);
    setFeedback("");
    try {
      await handleDeactivateAccountFeedback(feedback);
      setIsSubmittingFeedback(false);
    } catch (error) {
      setIsSubmittingFeedback(false);
    }
    setTimeout(() => navigate("/login"), 2000);
  };

  const handleConfirmDeactivation = async () => {
    setIsDeactivating(true);
    try {
      const response = await handleDeactivateAccount();
      if (response) {
        setTimeout(() => {
          setIsDeactivating(false);
          setOpenDeactivateModal(false);
          setOpenFeedbackModal(true);
        }, 1000);
      }
    } catch (error) {
      setIsDeactivating(false);
    }
  };

  const handleSubmitFeedback = async () => {
    setIsSubmittingFeedback(true);
    try {
      await handleDeactivateAccountFeedback(feedback);
      // handleCloseFeedbackModal();
    } catch (error) {
      setIsSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    if (info) {
      setValue('first_name', info?.first_name || '');
      setValue('last_name', info?.last_name || '');
      setValue('phone_number', info?.info?.phone_number || '');
      setValue('email', info?.email || '');
    }
  }, [info]);

  useEffect(() => {
    if (updateAccountStatus === SUCCESS) {
      if (account_type !== treatment_program) fetchInfo();
      else fetchFacilityInfo();
      handleSettingsState({ updateAccountStatus: "" });
    }
  }, [updateAccountStatus, account_type]);

  useEffect(() => {
    if (Object.keys(info)?.length > 0 && !Boolean(info?.email_verified_at)) {
      handleOpenAlert();
    }
  }, [info]);

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: 3,
      }}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>
          Account
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account name, email, and password.
        </Typography>
      </Box>

      <Box>
        <Typography variant="body1" mb={1.5}>
          Account name
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Controller
              name="first_name"
              control={control}
              disabled={!permissions?.notification_update}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="First Name"
                  fullWidth
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                  InputLabelProps={{ shrink: !!(field.value && field.value.length > 0) || field.value === 0 }}
                />
              )}
            />
            {/* <TextField
              fullWidth
              label="First name"
              onChange={handleInput}
              value={state.first_name || ""}
              name="first_name"
              error={isInvalid && !validateName(state.first_name)}
              helperText={
                isInvalid &&
                !validateName(state.first_name) &&
                "Please add valid first name"
              }
            /> */}
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="last_name"
              control={control}
              disabled={!permissions?.notification_update}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Last Name"
                  fullWidth
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                  InputLabelProps={{ shrink: !!(field.value && field.value.length > 0) || field.value === 0 }}
                />
              )}
            />
            {/* <TextField
              fullWidth
              label="Last name"
              onChange={handleInput}
              value={state.last_name || ""}
              name="last_name"
              error={isInvalid && !validateName(state.last_name)}
              helperText={
                isInvalid &&
                !validateName(state.last_name) &&
                "Please add validated last name"
              }
            /> */}
          </Grid>
        </Grid>
      </Box>

      <Box>
        <Typography variant="body1" mb={1.5}>
          Phone
        </Typography>
        {/* <PhoneNumberInput
          fullWidth
          label="Phone Number"
          onChange={handlePhoneNumber}
          value={state.phone_number || ""}
          name="phone_number"
          error={isInvalid && !validatePhoneNumber(state.phone_number)}
          helperText={
            isInvalid &&
            !validatePhoneNumber(state.phone_number) &&
            "Please add valid phone number"
          }
        /> */}
        <PhoneNumberInput
          fullWidth
          variant='outlined'
          control={control}
          sx={{ mb: 3 }}
          name='phone_number'
          label='Phone Number'
          value={state.phone_number}
          onChange={handlePhoneNumberInput}
          error={!!errors.phone_number}
          helperText={errors.phone_number?.message}
          trigger={trigger}
          disabled={!permissions?.notification_update}
        />
      </Box>

      <Box>
        <Typography variant="body1" mb={1.5}>
          Email
        </Typography>
        <Controller
          name="email"
          control={control}
          disabled={!permissions?.notification_update}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              label="Email"
              type="email"
              fullWidth
              error={!!errors.email || Boolean(emailExists)}
              helperText={
                errors.email?.message ||
                (Boolean(emailExists) && "This email already exists")
              }
              InputLabelProps={{ shrink: !!(field.value && field.value.length > 0) || field.value === 0 }}
            />
          )}
        />
        {/* <TextField
          variant="outlined"
          label="Email"
          type="email"
          fullWidth
          onChange={handleInput}
          value={state.email || ""}
          name="email"
          error={
            Boolean(emailExists) ||
            (isInvalid && (!state.email || !isEmail(state.email)))
          }
          helperText={
            Boolean(emailExists) &&
            "Email already exists please choose another one"
          }
        /> */}
      </Box>

      {openAlert && (
        <Alert
          severity="error"
          sx={{
            backgroundColor: "#FAF4D4",
            borderRadius: 1,
            border: "1px solid #EECD63",
            py: 0.5,
            px: 3,
            alignItems: "center",
          }}
          icon={<ShieldExclamation />}
        >
          Please check your inbox under {info?.email} to confirm your email.
          Can't find verification email?{" "}
          <LoadingButton
            size="small"
            sx={{
              p: 0,
              minWidth: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
            onClick={resendEmail}
            loading={resendEmailStatus === LOADING}
            disabled={!permissions?.notification_update}
          >
            Resend
          </LoadingButton>
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 4,
        }}
      >
        {account_type === "therapist" && !isMentalHealthProfessional() && (
          <LoadingButton
            size="large"
            sx={{
              px: 2,
              color: "#A3A6A8",
              backgroundColor: "#F1F2F3",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
            onClick={handleOpenDeactivateModal}
            disabled={!permissions?.notification_update}
          >
            Deactivate Account
          </LoadingButton>
        )}
        <LoadingButton
          variant="contained"
          size="large"
          sx={{
            minWidth: 110,
          }}
          type="submit"
          loading={updateAccountStatus === LOADING}
          disabled={!permissions?.notification_update}
        >
          Save
        </LoadingButton>
      </Box>

      <Modal
        open={openDeactivateModal}
        onClose={handleCloseDeactivateModal}
        aria-labelledby="deactivate-modal-title"
        aria-describedby="deactivate-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2.5,
          }}
        >
          <Typography
            id="deactivate-modal-title"
            variant="h4"
            component="h2"
            mb={2}
          >
            Are you sure you want to deactivate your account?
          </Typography>
          <Typography id="deactivate-modal-description" variant="body1" mb={4}>
            Deactivating your account will log you out and remove your profile
            from the community. You can reactivate your account anytime by
            emailing us at <a href="mailto:team@thettn.com">team@thettn.com</a>.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <LoadingButton
              onClick={handleCloseDeactivateModal}
              sx={{
                color: "#A3A6A8",
                backgroundColor: "#F1F2F3",
              }}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={handleConfirmDeactivation}
              disabled={isDeactivating}
            >
              {isDeactivating ? "Deactivating..." : "Confirm Deactivation"}
            </LoadingButton>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openFeedbackModal}
        onClose={handleCloseFeedbackModal}
        aria-labelledby="feedback-modal-title"
        aria-describedby="feedback-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2.5,
          }}
        >
          <Typography
            id="feedback-modal-title"
            variant="h4"
            component="h2"
            mb={2}
          >
            We’re sorry to see you go. Your account has been deactivated.
          </Typography>
          <Typography
            id="feedback-modal-description"
            variant="body1"
            mb={2}
            color="#61676B"
          >
            You’ve been logged out, and your profile is no longer visible on
            this site. You’re welcome to rejoin at any time. Email us at {" "}
            <a href="mailto:team@thettn.com">team@thettn.com</a>, and we’ll help you reactivate your account.
          </Typography>
          <Typography
            id="feedback-modal-description-2"
            variant="body1"
            mb={2}
            color="#61676B"
          >
            Have a moment? We’d love to hear what could’ve made your experience
            better.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Share your feedback here..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={isSubmittingFeedback}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <LoadingButton
              onClick={handleCloseFeedbackModal}
              sx={{
                color: "#A3A6A8",
                backgroundColor: "#F1F2F3",
              }}
              disabled={isSubmittingFeedback}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={handleSubmitFeedback}
              disabled={isSubmittingFeedback && !feedback.trim()}
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default AccountSettings;
